import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../features/userSlice";
import GenericEndPointsLogic from "../logic/GenericEndPointsLogic/GenericEndPointsLogic";

export const useGenericBusinessLogic = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const oauthTokenHeader = useSelector(selectUserToken) as string;

  // Helper function to extract error message from various error formats
  function extractErrorMessage(error: any): string {
    if (error instanceof Error) {
      return error.message;
    }

    // Shape: { text: '{"reason":"error","data":[{"reason":"...","created":false}]}', status: 400 }
    if (error?.text) {
      try {
        const parsed = JSON.parse(error.text);
        const failedReasons = (parsed?.data ?? [])
          .filter((item: any) => !item.created && item.reason)
          .map((item: any) => item.reason)
          .join(" | ");

        return (
          failedReasons || parsed?.reason || "An unexpected error occurred."
        );
      } catch {
        return error.text; // fallback: return raw text if JSON.parse fails
      }
    }

    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message;

    return "Failed to create tasks due to an unknown error."; // generic fallback
  }

  // function to create tasks
  const createTasks = async (multiTaskRequestBody: any, closeModal: any) => {
    setLoading(true);
    try {
      const res = await GenericEndPointsLogic.createNewTasks(
        oauthTokenHeader,
        multiTaskRequestBody,
      );

      if (res?.status !== 200 && res?.status !== 201) {
        throw new Error(`Failed to Create new task.`);
      }

      const allTasks: any[] = res?.data?.data ?? [];

      // Extract failed tasks from the data array
      const failedTasks = allTasks.filter((item: any) => !item.created);

      if (failedTasks.length > 0) {
        // Join multiple failure reasons with " | "
        const errorMessage = failedTasks
          .map((item: any) => item.reason)
          .filter(Boolean)
          .join(" | ");

        throw new Error(errorMessage || "Some tasks failed to create.");
      }

      // All tasks created successfully
      closeModal();
      dispatch(
        setDisplayErrorSnackBar({
          message: `${allTasks.length} task${allTasks.length > 1 ? "s" : ""} created successfully.`,
          type: "success",
        }),
      );
    } catch (error) {
      console.error("Error creating tasks:", error);
      const errorMessage = extractErrorMessage(error);

      dispatch(
        setDisplayErrorSnackBar({
          message: `${errorMessage}`,
          type: "error",
        }),
      );
      return; // Stop execution, do not close modal
    } finally {
      setLoading(false);
    }
  };

  return { createTasks, loading };
};
